package com.crowpay.views.components.project.activity

import com.crowpay.*
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.sdk.currentSession
import com.crowpay.extensions.fullName
import com.crowpay.extensions.isUnread
import com.crowpay.extensions.markAsReadUpdateOnRemove
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.*
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.nav.viewIsWide
import com.crowpay.views.components.unreadIconWhen
import com.crowpay.views.screens.common.ProjectLens
import com.crowpay.views.screens.common.ProjectView
import com.crowpay.views.theming.*
import com.lightningkite.UUID
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.ViewWrapper
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.reactive.onRemove
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.l2.*
import com.lightningkite.lightningdb.Condition
import com.lightningkite.lightningdb.Query
import com.lightningkite.lightningdb.condition
import com.lightningkite.lightningdb.eq
import com.lightningkite.lightningdb.sort
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlin.time.DurationUnit

private fun String.truncate(take: Int, postfix: String = "..."): String =
    if (length > take) take(take) + postfix
    else this

@Routable("project/messages/{project}/{lens}")
class ProjectMessageThread(val project: UUID, val lens: ProjectLens) : Page {
    override val title: Readable<String> = shared { notNullSession().projects[project]()?.name ?: "" }

    private class MessageRenderers(
        val lens: ProjectLens,
        val onReply: suspend (Pair<ProjectMessage?, ProjectMessage>) -> Unit
    ) : RecyclerViewRendererSet<Pair<ProjectMessage?, ProjectMessage>, UUID> {
        override fun id(item: Pair<ProjectMessage?, ProjectMessage>): UUID = item.second._id
        val isClient = when (lens) {
            ProjectLens.Contractor -> false
            ProjectLens.Customer -> true
            ProjectLens.Preview -> true
        }

        private fun ViewWriter.renderDateTimeSubHeader(message: Readable<Pair<ProjectMessage?, ProjectMessage>>) {
            stack {
                existsDefaultFalse {
                    val first = message().first
                    val second = message().second
                    if (first == null) true
                    else if (second.sent.minus(first.sent).toLong(DurationUnit.SECONDS) > 3600) true
                    else false
                }
                centered - MessageSectionSubHeaderSemantic.onNext - padded - row {
                    sizeConstraints(width = 3.rem) - centered - col { greySeparator() }
                    centered - body {
                        ::content {
                            message().second.sent.format(Formats.eeeddmmyyyy_hmm)
                        }
                        align = Align.Center
                    }
                    sizeConstraints(width = 3.rem) - centered - col { greySeparator() }
                }
            }
        }

        private fun ViewWriter.renderUserName(message: Readable<Pair<ProjectMessage?, ProjectMessage>>) {
            compact - row {
                existsDefaultFalse {
                    val first = message().first
                    val second = message().second
                    if (first == null) true
                    else if (first.sender != second.sender) true
                    else if (second.sent.minus(first.sent).toLong(DurationUnit.SECONDS) > 600) true
                    else false
                }
                row {
                    smallBody {
                        ::content {
                            message().second.senderName
                        }
                    }
                }
            }
        }

        private fun ViewWriter.renderUserIcon(message: Readable<Pair<ProjectMessage?, ProjectMessage>>) {
            row {
                ::visible {
                    val first = message().first
                    val second = message().second
                    if (first == null) true
                    else if (first.sender != second.sender) true
                    else if (second.sent.minus(first.sent).toLong(DurationUnit.SECONDS) > 600) true
                    else false
                }
                sizeConstraints(height = 1.9.rem, aspectRatio = 1.0) - InitialIconSemantic.onNext - row {
                    existsDefaultFalse {
                        message().second.senderIcon == null
                    }
                    spacing = 0.rem
                    centered - expanding - text {
                        ::content {
                            align = Align.Center
                            currentSession.awaitNotNull().self()
                                .let { "${it.firstName.take(1)}${it.lastName.take(1)}" }
                        }
                    }
                }
                sizeConstraints(height = 2.rem, width = 2.rem) - image {
                    existsDefaultFalse {
                        message().second.senderIcon != null
                    }
                    ::source {
                        val logo = message().second.senderIcon
                        if (logo != null && logo.location != "") {
                            ImageRemote(
                                logo.location
                            )
                        } else {
                            null
                        }
                    }
                    description = "Sender Icon"
                }
            }
        }

        private fun ViewWriter.renderReplyOrReference(message: Readable<Pair<ProjectMessage?, ProjectMessage>>) {
            // NOTE: The Reference functionality is no longer used.
            row {
                existsDefaultFalse {
                    message().let { it.second.replyingToMessage != null || it.second.referencesChangeRequest != null || it.second.referencesPayApp != null }
                }
                dynamicTheme {
                    message().let {
                        if (it.second.referencesChangeRequest != null || it.second.referencesPayApp != null) DangerSemantic
                        else if (it.second.replyingToMessage != null) SmallBodySemantic
                        else null
                    }
                }
                centered - icon {
                    existsDefaultFalse {
                        message().let { it.second.referencesChangeRequest != null || it.second.referencesPayApp != null }
                    }
                    source = Icon.activity.resize(1.rem)
                    description = "Message References"
                }
                expanding - centered - body {
                    ::content {
                        message().run {
                            second.referencesChangeRequest?.reason
                                ?: second.referencesPayApp?.reason
                                ?: second.replyingToMessage?.message?.truncate(100)
                        } ?: ""
                    }
                }
            }
        }

        inner class MyMessage : RecyclerViewRenderer<Pair<ProjectMessage?, ProjectMessage>> {
            override fun render(
                viewWriter: ViewWriter,
                data: Readable<Pair<ProjectMessage?, ProjectMessage>>,
                index: Readable<Int>
            ): ViewModifiable = with(viewWriter) {
                col {
                    spacing = 0.rem
                    renderDateTimeSubHeader(data)
                    row {
                        expanding - space()
                        sizeConstraints(maxWidth = AppDimensions.pageWidth * 0.8) - col {
                            spacing = 5.dp
                            MessageSemantic(mine = true).onNext - stack {
                                compact - col {
                                    renderReplyOrReference(data)
                                    body {
                                        ::content { data().second.message }
                                    }
                                }
                            }
                            atEnd - smallBody { ::content { data().second.sent.formatRelative() } }
                        }
                    }
                }
            }
        }

        inner class OthersMessage : RecyclerViewRenderer<Pair<ProjectMessage?, ProjectMessage>> {
            private fun ViewWriter.messageBlock(data: Readable<Pair<ProjectMessage?, ProjectMessage>>) =
                col {
                    spacing = 5.dp
                    row {
                        spacing = AppDimensions.expandButtonSpace
                        centered - unreadIconWhen { data().second.isUnread(notNullSession().userId) }
                        renderUserName(data)
                    }
                    MessageSemantic(mine = false).onNext - stack {
                        compact - col {
                            renderReplyOrReference(data)
                            body {
                                ::content { data().second.message }
                            }
                        }
                    }
                    compact - row {
                        smallBody { ::content { data().second.sent.formatRelative() } }
                        smallBody(BulletPoint)
                        textButton - button {
                            spacing = 0.px
                            smallBody("Reply")
                            onClick("reply") { onReply(data()) }
                        }
                    }
                }

            override fun render(
                viewWriter: ViewWriter,
                data: Readable<Pair<ProjectMessage?, ProjectMessage>>,
                index: Readable<Int>
            ): ViewModifiable = with(viewWriter) {
                col {
                    spacing = 0.rem
                    renderDateTimeSubHeader(data)
                    if (viewIsWide) sizeConstraints(maxWidth = AppDimensions.pageWidth * 0.8) - row {
                        spacing = 0.5.rem
                        renderUserIcon(data)
                        messageBlock(data)
                    }
                    else gravity(Align.Start, Align.Center) - messageBlock(data)
                }
            }
        }

        override fun renderer(item: Pair<ProjectMessage?, ProjectMessage>): RecyclerViewRenderer<Pair<ProjectMessage?, ProjectMessage>> =
            if (isClient == item.second.clientMessage) MyMessage()
            else OthersMessage()
    }

    class Renderer(
        val lens: ProjectLens,
        val project: UUID,
        val buffer: Int = 15,
        val increment: Int = 100,
        val requestFocusOnStart: Boolean = false
    ) {
        val _messages = shared {
            currentSession()
                ?.projectMessages?.watch(
                    Query(
                        condition {
                            Condition.And(it.project eq project, it.referencesPayApp eq null, it.referencesChangeRequest eq null)
                        },
                        orderBy = sort { it.sent.descending() },
                        limit = 100
                    )
                )
                ?: ConstantLimitReadable(emptyList())
        }
        val messages = shared {
            val raw = _messages()()
            val result = raw.mapIndexed { index, message ->
                raw.getOrNull(index + 1) to message
            }
            result
        }

        private val newMessage = Property("")
        private val replyingTo = Property<ProjectMessage?>(null)

        fun renderTo(writer: ViewWriter, setTarget: Boolean = false) = with(writer) {
            col {
                launch {
                    notNullSession().projectMessages.markAsReadUpdateOnRemove(this@col, messages().map { it.second })
                }
                expanding - (if (viewIsWide) sizeConstraints(minHeight = AppDimensions.activityTabMinSize) else ViewWrapper) - stack {
                    val messageRecycler = recyclerView {
                        val nowRead = HashSet<UUID>()

                        onRemove {
                            if (nowRead.isEmpty()) return@onRemove
                            CoroutineScope(Dispatchers.Default).launch {
                                val updatedMessages =
                                    notNullSession().nonCached.projectMessage.markAsRead(nowRead.toList()).associateBy { it._id }
                                notNullSession().projectMessages.localSignalUpdate(
                                    matching = { it._id in updatedMessages.keys },
                                    modify = { updatedMessages[it._id] }
                                )
                            }
                        }

                        reactive {
                            _messages()().takeUnless { it.isEmpty() }
                                ?.filter { it.isUnread(notNullSession().userId) }
                                ?.map { it._id }
                                ?.let { nowRead.addAll(it) }
                        }

                        reactive {
                            val limiter = _messages()
                            if (limiter().size == limiter.limit && firstIndex() <= buffer) {
                                limiter.limit += increment
                            }
                        }

                        rendererSet = MessageRenderers(lens) { replyingTo.value = it.second }

                        reactive {
                            data = RecyclerViewData.fromList(messages().reversed())
                            // will scroll down when messages update, so when you submit a message it will scroll up into view
                            // if messages later add a webhook so it will update when another party sends a message
                            // this should probably update to only scroll down when the latest update is your own message
                            scrollToIndex(messages().lastIndex, align = Align.End, animate = true)
                        }
                        launch {
                            scrollToIndex(messages().lastIndex, align = Align.End, animate = false)
                        }
                    }
                    atBottomCenter - compact - buttonTheme - button {
                        ::exists {
                            messageRecycler.lastIndex() < messages().lastIndex - 6
                        }
                        themeChoice += SetCornerRadii { CornerRadii.Constant(10.dp) }
                        smallBody("scroll to bottom")
                        onClick {
                            messageRecycler.scrollToIndex(messages().lastIndex, align = Align.End, animate = true)
                        }
                    }
                }
                fieldTheme - col {
                    if (setTarget) JumpTo.ProjectMessages.setTarget(this)

                    removeOutline - lightSection - stack {
                        existsDefaultFalse { replyingTo() != null }
                        body {
                            ::content { "Replying To:\n${replyingTo()?.message?.truncate(200)}" }
                        }
                        atTopEnd - button {
                            spacing = 3.dp
                            icon(Icon.close.resize(1.rem), "Stop Replying")
                            onClick { replyingTo.value = null }
                        }
                    }
                    stack {
                        if (viewIsWide) sizeConstraints(height = 5.rem)
                        textArea {
                            if (requestFocusOnStart) requestFocus()
                            content bind newMessage
                        }
                        if (viewIsWide) atBottomEnd - compact - removeOutline - row {
                            clearButton()
                            sendButton()
                        }
                    }
                }
                if (viewIsNarrow) row {
                    expanding - clearButton()
                    expanding - sendButton()
                }
            }
        }

        private fun ViewWriter.clearButton() = secondaryButton - button {
            ::enabled { newMessage().isNotBlank() }
            specCenteredText("Clear")
            onClick("discard") {
                newMessage.value = ""
                replyingTo.value = null
            }
        }

        private fun ViewWriter.sendButton() = primaryButton - button {
            ::enabled { newMessage().isNotBlank() }
            specCenteredText("Send")
            onClick("send") {
                val session = notNullSession()
                session.projectMessages.insert(
                    ProjectMessage(
                        project = project,
                        sender = session.userId,
                        senderName = session.self().fullName,
                        message = newMessage.value,
                        replyingToMessage = replyingTo.value?.let { MessageReply(it) },
                        clientMessage = lens != ProjectLens.Contractor
                    )
                )
                replyingTo.value = null
                newMessage.value = ""
            }
        }
    }

    override fun ViewWriter.render2(): ViewModifiable = Renderer(lens, project).renderTo(this)

    class Dialog(val lens: ProjectLens, val project: Project) : Page {
        override fun ViewWriter.render2(): ViewModifiable = dismissBackground {
            spacing = AppDimensions.sectionIndent
            dialog - gravity(Align.Center, Align.Stretch) - sizeConstraints(width = AppDimensions.pageWidth) - stack {
                spacing = 3.dp
                padded - stack {
                    spacing = AppDimensions.fullIndent / 2
                    removeOutline - col {
                        title("${project.name} Messaging")
                        expanding - Renderer(lens, project._id, requestFocusOnStart = true).renderTo(this)
                    }
                }
                atTopEnd - removeOutline - buttonTheme - button {
                    icon(Icon.close, "Close messages")
                    onClick("Close Messages") { dialogScreenNavigator.dismiss() }
                }
            }
        }
    }
}

fun ProjectView.projectMessages(writer: ViewWriter): ViewModifiable = with(writer) {
    if (viewIsNarrow) sizeConstraints(height = AppDimensions.activityTabMinSize + 8.rem)
    ProjectMessageThread.Renderer(lens, projectId).renderTo(writer, setTarget = true)
}